import { useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  useParametrizacoes, useSalvarParametrizacao, useRemoverParametrizacao,
} from "@/features/contabil/hooks/useContabilizacao";
import {
  ContaContabilSearchCombobox, rotuloConta, usePlanoContasLookup,
} from "@/components/ContaContabilSearchCombobox";
import { TIPO_EVENTO_LABEL, type TipoEventoContabil } from "@/features/contabil/types/contabilizacao";

export default function ParametrizacaoContabilPage() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;
  const { data: paramList = [], isLoading } = useParametrizacoes(empresaId);
  // Só contas analíticas (folha) podem ser parametrizadas; sintéticas somam os filhos.
  const { data: contas = [] } = usePlanoContasLookup();
  const nomeConta = (id: number, codigo?: string | null) =>
    rotuloConta(contas.find((c) => c.id === id)) || codigo || "—";
  const salvar = useSalvarParametrizacao();
  const remover = useRemoverParametrizacao();

  const [open, setOpen] = useState(false);
  const [tipoEvento, setTipoEvento] = useState<number>(1);
  const [contaDebito, setContaDebito] = useState<number>(0);
  const [contaCredito, setContaCredito] = useState<number>(0);
  const [descricao, setDescricao] = useState("");
  const [isAtivo, setIsAtivo] = useState(true);

  const eventosOpts: TipoEventoContabil[] = [1, 2, 3, 4, 5, 6, 7, 8, 99];

  const handleSalvar = async () => {
    await salvar.mutateAsync({
      empresaId,
      tipoEvento: tipoEvento as TipoEventoContabil,
      contaContabilDebitoId: contaDebito,
      contaContabilCreditoId: contaCredito,
      descricao,
      isAtivo,
    });
    setOpen(false);
    setDescricao("");
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Parametrização Contábil</h1>
          <p className="text-muted-foreground">
            Define qual conta debitar e creditar para cada evento de negócio.
            Sem parametrização ativa, o evento não gera lançamento (só log).
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Novo mapeamento</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Mapear evento → contas</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Evento</Label>
                <Select value={String(tipoEvento)} onValueChange={(v) => setTipoEvento(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {eventosOpts.map((e) => (
                      <SelectItem key={e} value={String(e)}>{TIPO_EVENTO_LABEL[e]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Conta a debitar</Label>
                <ContaContabilSearchCombobox
                  apenasAnaliticas
                  value={contaDebito || null}
                  onChange={(id) => setContaDebito(id ?? 0)}
                />
              </div>
              <div>
                <Label>Conta a creditar</Label>
                <ContaContabilSearchCombobox
                  apenasAnaliticas
                  value={contaCredito || null}
                  onChange={(id) => setContaCredito(id ?? 0)}
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={isAtivo} onCheckedChange={setIsAtivo} />
                <Label>Ativo</Label>
              </div>
              <Button onClick={handleSalvar} disabled={salvar.isPending || !contaDebito || !contaCredito || contaDebito === contaCredito}>
                {salvar.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Salvar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Mapeamentos cadastrados</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-32" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Evento</TableHead>
                  <TableHead>Débito</TableHead>
                  <TableHead>Crédito</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paramList.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Nenhum mapeamento cadastrado.
                  </TableCell></TableRow>
                ) : paramList.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{TIPO_EVENTO_LABEL[p.tipoEvento]}</TableCell>
                    <TableCell className="text-sm">{nomeConta(p.contaContabilDebitoId, p.codigoContaDebito)}</TableCell>
                    <TableCell className="text-sm">{nomeConta(p.contaContabilCreditoId, p.codigoContaCredito)}</TableCell>
                    <TableCell>{p.descricao}</TableCell>
                    <TableCell>
                      <Badge className={p.isAtivo ? "bg-green-500/10 text-green-600" : "bg-gray-500/10"}>
                        {p.isAtivo ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => remover.mutate(p.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
